import { z } from 'zod';
import type { ApprovalStatus } from '../enums';
import { computeAmount, formatPLN, type Money } from '../money';
import { formatDuration } from '../time';
import type { ApprovalStateRef } from './approvals';
import {
  amountSchema,
  CURRENCIES,
  type Currency,
  currencySchema,
  emailSchema,
  idSchema,
  localDateSchema,
  markdownSchema,
  rateSchema,
  versionSchema,
} from './common';

/**
 * Oferty (docs/3VTOS_MVP.md §8): `offers`, `offer_versions` (packages/db/src/schema/offers.ts).
 * Moduł uprawnień: `clients` (DECISIONS); kwoty i stawki wymagają „Widzi stawki i kwoty”, zmiana stawek lub warunków
 * i wysyłka = Approval (CONVENTIONS §0.4).
 *
 * Statusy: robocza → do akceptacji właściciela → wysłana → przyjęta | odrzucona (przyjęcie przez klienta
 * rejestrujemy ręcznie). Każda wysłana wersja jest zamrożona (`frozen`, `sent_at`, `sent_to`): edycja po wysyłce =
 * nowa wersja (`current_version` + 1), stara treść zostaje bez zmian.
 * Usługi: packages/core/src/modules/offers (router tRPC `offers`).
 */

export const OFFER_STATUSES = ['draft', 'pending_owner', 'sent', 'accepted', 'rejected'] as const;
export type OfferStatus = (typeof OFFER_STATUSES)[number];
export const offerStatusSchema = z.enum(OFFER_STATUSES);

/**
 * Statusy, w których treść bieżącej (niezamrożonej) wersji można edytować bez nowej wersji. W `pending_owner`
 * edytuje tylko właściciel albo admin (autor najpierw cofa ofertę do roboczej).
 */
export const EDITABLE_OFFER_STATUSES = ['draft', 'pending_owner'] as const satisfies readonly OfferStatus[];

/** Statusy „otwartych” ofert (lista: zakładka Otwarte). */
export const OPEN_OFFER_STATUSES = ['draft', 'pending_owner', 'sent'] as const satisfies readonly OfferStatus[];

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

/**
 * Treść wersji oferty (jsonb `offer_versions.content`). Czas w sekundach (konwencja), np. 40 h = 144 000 s.
 * `package` = opis pakietu godzin („20 h miesięcznie”), `examples` = przykłady bez stałych cen wszystkich usług.
 * Pakiet godzin: `estimatedSeconds` = liczba godzin pakietu, `package` = opis (okres, zasady).
 */
export const offerContentSchema = z.object({
  scope: markdownSchema.default(''),
  result: markdownSchema.default(''),
  exclusions: markdownSchema.default(''),
  assumptions: markdownSchema.default(''),
  /** szacowany czas albo wielkość pakietu w sekundach (null = bez szacunku) */
  estimatedSeconds: z.number().int().positive().nullable().optional(),
  package: optionalText(500),
  /** stawka netto/h (wymaga „Widzi stawki i kwoty”) */
  rate: rateSchema.nullable().optional(),
  currency: currencySchema.default('PLN'),
  /** termin orientacyjny, np. „6 tygodni od akceptacji” */
  timeline: optionalText(500),
  /** warunki współpracy */
  terms: markdownSchema.default(''),
  examples: markdownSchema.nullable().optional(),
});
export type OfferContent = z.infer<typeof offerContentSchema>;

/** Pola treści w kolejności wyświetlania (podgląd, porównanie wersji, mail). */
export const OFFER_CONTENT_FIELDS = [
  'scope',
  'result',
  'exclusions',
  'assumptions',
  'estimatedSeconds',
  'package',
  'rate',
  'timeline',
  'terms',
  'examples',
] as const;
export type OfferContentField = (typeof OFFER_CONTENT_FIELDS)[number];

/** Pola Markdown (sekcje treści). */
export const OFFER_TEXT_FIELDS = ['scope', 'result', 'exclusions', 'assumptions', 'terms', 'examples'] as const;

/** Pola pieniężne treści i wersji (bez „Widzi stawki i kwoty”: pominięte). */
export const OFFER_MONEY_FIELDS = ['rate', 'totalNet'] as const;

/**
 * „Stawka i warunki oferty” (MVP §10: potwierdzenie właściciela). Zmiana tych pól przez AI albo MCP nie zapisuje się
 * wprost, tylko tworzy akceptację `offers.terms_change`; właściciel w UI zmienia je bezpośrednio.
 */
export const OFFER_TERMS_FIELDS = ['rate', 'currency', 'estimatedSeconds', 'package', 'terms'] as const;
export type OfferTermsField = (typeof OFFER_TERMS_FIELDS)[number];

/** Wartość netto wersji = szacowany czas × stawka (HALF_UP do grosza); brak czasu albo stawki = null. */
export function computeOfferTotal(content: Pick<OfferContent, 'estimatedSeconds' | 'rate'>): Money | null {
  if (!content.estimatedSeconds || !content.rate) return null;
  return computeAmount(content.rate, content.estimatedSeconds);
}

/** Treść z kompletem kluczy (brak = null / ''), np. do hasha wersji i porównania. */
export interface NormalizedOfferContent {
  scope: string;
  result: string;
  exclusions: string;
  assumptions: string;
  estimatedSeconds: number | null;
  package: string | null;
  rate: string | null;
  currency: Currency;
  timeline: string | null;
  terms: string;
  examples: string | null;
}

export function normalizeOfferContent(c: Partial<OfferContent> | Record<string, unknown>): NormalizedOfferContent {
  const v = c as Partial<OfferContent>;
  const text = (s: unknown) => (typeof s === 'string' ? s : '');
  const opt = (s: unknown) => (typeof s === 'string' && s.trim() !== '' ? s : null);
  return {
    scope: text(v.scope),
    result: text(v.result),
    exclusions: text(v.exclusions),
    assumptions: text(v.assumptions),
    estimatedSeconds: typeof v.estimatedSeconds === 'number' && v.estimatedSeconds > 0 ? v.estimatedSeconds : null,
    package: opt(v.package),
    rate: opt(v.rate),
    currency: (CURRENCIES as readonly string[]).includes(v.currency as string) ? (v.currency as Currency) : 'PLN',
    timeline: opt(v.timeline),
    terms: text(v.terms),
    examples: opt(v.examples),
  };
}

const offerTitleSchema = z
  .string()
  .trim()
  .min(1, { message: 'Tytuł jest wymagany' })
  .max(300, { message: 'Najwyżej 300 znaków' });

export const offerFieldsSchema = z.object({
  clientId: idSchema,
  title: offerTitleSchema,
  content: offerContentSchema,
});
export type OfferFields = z.infer<typeof offerFieldsSchema>;

/** Odbiorcy wysyłki wersji (zapisywani w `offer_versions.sent_to`). */
export const offerRecipientsSchema = z.array(emailSchema).min(1, { message: 'Podaj odbiorcę' }).max(20);

export const OFFER_MESSAGES = {
  frozen: 'Ta wersja została wysłana i jest zamrożona. Zmiany tworzą nową wersję.',
  closed: 'Ta wersja jest zamknięta (oferta przyjęta albo odrzucona). Zmiany tworzą nową wersję.',
  notDraft: 'Oferta nie jest robocza',
  pendingOwner: 'Oferta czeka na akceptację właściciela. Cofnij ją do roboczej, żeby ją zmienić.',
  ownerSends: 'Ofertę wysyła właściciel. Przekaż ją do akceptacji właściciela.',
  ownerNoSubmit: 'Jako właściciel możesz od razu wysłać ofertę do klienta.',
  draftNotFrozen: 'Bieżąca wersja jest robocza. Zmień ją zamiast tworzyć nową wersję.',
  accepted: 'Oferta została przyjęta. Zmiana zakresu to nowa oferta.',
  sendPending: 'Wysyłka tej wersji czeka już w Akceptacjach.',
  sendStale: 'Treść oferty zmieniła się po prośbie o wysyłkę. Poproś o akceptację wysyłki jeszcze raz.',
  sendClosed: 'Ta wersja oferty jest już zamknięta. Nic nie zostało wysłane.',
  scopeRequired: 'Uzupełnij zakres oferty przed wysyłką.',
  recipientNotClient: 'Odbiorca nie jest kontaktem tego klienta.',
  recipientNoEmail: 'Kontakt nie ma adresu email.',
  dateFuture: 'Data nie może być w przyszłości.',
  clientArchived: 'Klient jest w archiwum.',
  noRates: 'Brak uprawnienia do stawek i kwot',
  termsChanged: 'Stawka albo warunki oferty zmieniły się w międzyczasie. Poproś o zmianę jeszcze raz.',
  manualOnly: 'Przyjęcie i odrzucenie oferty rejestruje osoba w aplikacji.',
  versionNotFound: 'Nie ma takiej wersji oferty.',
} as const;

/**
 * Powód wycofania czekającej akceptacji `offers.send` (Akceptacje: „Wycofana”): zgoda dotyczy konkretnej treści
 * i stanu oferty, więc zmiana treści, powrót do roboczej albo zamknięcie oferty ją unieważnia.
 */
export const OFFER_WITHDRAW_REASONS = {
  contentChanged: 'Treść oferty zmieniła się po prośbie o wysyłkę',
  backToDraft: 'Oferta wróciła do roboczej',
  closed: 'Oferta została zamknięta (przyjęta albo odrzucona)',
} as const;

// ── Wejścia usług ───────────────────────────────────────────────────────────

/** Zakładki listy ofert: otwarte (robocze, u właściciela, wysłane), przyjęte, odrzucone, wszystkie. */
export const OFFER_LIST_FILTERS = ['open', 'accepted', 'rejected', 'all'] as const;
export type OfferListFilter = (typeof OFFER_LIST_FILTERS)[number];

export const offerListInput = z
  .object({
    clientId: idSchema.optional(),
    filter: z.enum(OFFER_LIST_FILTERS).default('all'),
    q: z.string().trim().max(200).optional(),
    limit: z.number().int().min(1).max(500).default(200),
  })
  .strict();
export type OfferListInput = z.input<typeof offerListInput>;

export const offerIdInput = z.object({ id: idSchema }).strict();

/** Nowa oferta: klient, tytuł, treść wersji 1 (brak stawki = stawka klienta). */
export const createOfferInput = z
  .object({
    clientId: idSchema,
    title: offerTitleSchema,
    content: offerContentSchema.default({}),
  })
  .strict();
export type CreateOfferInput = z.input<typeof createOfferInput>;

/** Zmiana treści: tylko podane pola (reszta bez zmian — np. stawka, której osoba nie widzi). */
export const offerContentPatchSchema = offerContentSchema.partial().strict();
export type OfferContentPatch = z.infer<typeof offerContentPatchSchema>;

export const updateOfferDraftInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    title: offerTitleSchema.optional(),
    content: offerContentPatchSchema.optional(),
  })
  .strict();
export type UpdateOfferDraftInput = z.input<typeof updateOfferDraftInput>;

/** Operacje na ofercie z wersją rekordu (CONFLICT przy niezgodności): nowa wersja, cofnięcie do roboczej. */
export const offerVersionedInput = z.object({ id: idSchema, version: versionSchema }).strict();

export const submitOfferInput = z
  .object({ id: idSchema, version: versionSchema, note: z.string().trim().max(1000).optional() })
  .strict();

/** Ręczne przyjęcie / odrzucenie przez klienta (data w Europe/Warsaw, domyślnie dziś, nie z przyszłości). */
export const offerOutcomeInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    date: localDateSchema.optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .strict();
export type OfferOutcomeInput = z.input<typeof offerOutcomeInput>;

export const requestOfferSendInput = z
  .object({
    id: idSchema,
    version: versionSchema,
    /** kontakty klienta z adresem email */
    contactIds: z.array(idSchema).min(1, { message: 'Wybierz odbiorcę' }).max(10, { message: 'Najwyżej 10 odbiorców' }),
    subject: z.string().trim().min(1, { message: 'Temat jest wymagany' }).max(300, { message: 'Najwyżej 300 znaków' }),
    /** wstęp maila (treść oferty dokleja serwer) */
    message: z.string().trim().max(5000, { message: 'Najwyżej 5000 znaków' }).default(''),
  })
  .strict();
export type RequestOfferSendInput = z.input<typeof requestOfferSendInput>;

export const compareOfferVersionsInput = z
  .object({ id: idSchema, from: z.number().int().positive(), to: z.number().int().positive() })
  .strict();

export const mainOfferInput = z.object({ clientId: idSchema }).strict();

// ── Akceptacje (Approval) ───────────────────────────────────────────────────

/** Wysyłka wersji oferty mailem (MVP §8: akceptacja treści i odbiorcy). Zatwierdza „Wysyła maile”. */
export const OFFERS_SEND_APPROVAL = 'offers.send';
/** Zmiana stawki lub warunków oferty przez AI / MCP (MVP §10: potwierdzenie). Zatwierdza „Widzi stawki i kwoty”. */
export const OFFERS_TERMS_APPROVAL = 'offers.terms_change';

export const offerSendRecipientSchema = z.object({
  contactId: idSchema,
  email: emailSchema,
  name: z.string().trim().max(200).nullable(),
});
export type OfferSendRecipient = z.infer<typeof offerSendRecipientSchema>;

const sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/, { message: 'Nieprawidłowy skrót' });

export const offerSendPayloadSchema = z.object({
  offerId: idSchema,
  offerNumber: z.number().int().positive(),
  title: z.string().min(1).max(300),
  clientId: idSchema,
  clientName: z.string().max(300),
  versionId: idSchema,
  versionNo: z.number().int().positive(),
  /** sha256 treści wersji (tytuł, numer wersji, treść) w chwili prośby; inna treść przy wykonaniu = odmowa */
  versionHash: sha256HexSchema,
  to: z.array(offerSendRecipientSchema).min(1).max(10),
  subject: z.string().trim().min(1).max(300),
  body: z.string().min(1).max(200_000),
  totalNet: amountSchema.nullable(),
  currency: currencySchema,
  /** pierwsza linia zakresu (podsumowanie akceptacji) */
  scopeSummary: z.string().max(300),
});
export type OfferSendPayload = z.infer<typeof offerSendPayloadSchema>;

/** Wartości „stawki i warunków” (tylko zmieniane pola). */
export const offerTermsValuesSchema = z
  .object({
    rate: rateSchema.nullable().optional(),
    currency: currencySchema.optional(),
    estimatedSeconds: z.number().int().positive().nullable().optional(),
    package: z.string().max(500).nullable().optional(),
    terms: markdownSchema.optional(),
  })
  .strict();
export type OfferTermsValues = z.infer<typeof offerTermsValuesSchema>;

export const offerTermsChangePayloadSchema = z.object({
  offerId: idSchema,
  offerNumber: z.number().int().positive(),
  title: z.string().min(1).max(300),
  clientId: idSchema,
  versionId: idSchema,
  versionNo: z.number().int().positive(),
  /** wartości w chwili prośby (inne przy wykonaniu = CONFLICT) */
  before: offerTermsValuesSchema,
  after: offerTermsValuesSchema,
});
export type OfferTermsChangePayload = z.infer<typeof offerTermsChangePayloadSchema>;

// ── Wyjścia ─────────────────────────────────────────────────────────────────

export interface OfferClientRef {
  id: string;
  name: string;
  color: string | null;
}

export interface OfferUserRef {
  id: string;
  name: string;
}

/** Treść w DTO: bez „Widzi stawki i kwoty” pole `rate` jest pominięte. */
export type OfferContentDto = Omit<NormalizedOfferContent, 'rate'> & { rate?: string | null };

/** Stan wersji: robocza (edytowalna), wysłana (zamrożona przy wysyłce), zamknięta (zamrożona bez wysyłki). */
export const OFFER_VERSION_STATES = ['draft', 'sent', 'closed'] as const;
export type OfferVersionState = (typeof OFFER_VERSION_STATES)[number];

export function offerVersionState(v: { frozen: boolean; sentAt: Date | string | null }): OfferVersionState {
  if (!v.frozen) return 'draft';
  return v.sentAt ? 'sent' : 'closed';
}

export interface OfferVersionDto {
  id: string;
  versionNo: number;
  state: OfferVersionState;
  content: OfferContentDto;
  /** wartość netto (bez „Widzi stawki i kwoty”: pominięta) */
  totalNet?: Money | null;
  frozen: boolean;
  sentAt: Date | null;
  sentTo: string[] | null;
  approvalId: string | null;
  author: OfferUserRef | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface OfferListItem {
  id: string;
  number: number;
  /** wersja rekordu do edycji współbieżnej */
  version: number;
  clientId: string;
  client: OfferClientRef;
  title: string;
  status: OfferStatus;
  currentVersion: number;
  currency: string;
  /** bieżąca wersja: szacowany czas / wielkość pakietu */
  estimatedSeconds: number | null;
  package: string | null;
  /** bieżąca wersja (bez „Widzi stawki i kwoty”: pominięte) */
  totalNet?: Money | null;
  /** ostatnia wysyłka (dowolnej wersji) */
  lastSentAt: Date | null;
  acceptedAt: Date | null;
  rejectedAt: Date | null;
  createdBy: OfferUserRef | null;
  createdAt: Date;
  updatedAt: Date;
}

/** Co osoba może zrobić z ofertą teraz (UI ukrywa przyciski; backend i tak sprawdza). */
export interface OfferPermissions {
  edit: boolean;
  newVersion: boolean;
  submitToOwner: boolean;
  backToDraft: boolean;
  requestSend: boolean;
  markOutcome: boolean;
}

/** Wysyłka czekająca w Akceptacjach (`stale` = treść zmieniła się po prośbie, wykonanie odmówi). */
export interface OfferPendingSend {
  approvalId: string;
  versionNo: number;
  status: ApprovalStatus;
  stale: boolean;
  createdAt: Date;
}

/**
 * Stan ostatniej prośby o wysyłkę (kompozycja Fali 3): czeka / wysyłana / wysłana (z oznaczeniem symulacji) / błąd /
 * odrzucona / wycofana / wygasła — oferta pokazuje go w banerze. Bez treści maila i odbiorców (to jest w Akceptacjach).
 */
export interface OfferSendApprovalState extends ApprovalStateRef {
  versionNo: number;
}

export interface OfferDetail extends OfferListItem {
  versions: OfferVersionDto[];
  permissions: OfferPermissions;
  pendingSend: OfferPendingSend | null;
  /** ostatnia akceptacja `offers.send` tej oferty (dowolny status); null = nigdy nie wysyłana przez Akceptacje */
  sendApproval: OfferSendApprovalState | null;
  /** stawka / warunki od AI lub MCP czekają w Akceptacjach (tylko w odpowiedzi `create` i `updateDraft`) */
  termsApprovalId?: string | null;
}

export interface OfferSendContact {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
}

export interface OfferSendOptions {
  contacts: OfferSendContact[];
  defaultContactIds: string[];
  subject: string;
  message: string;
  /** treść oferty w mailu (bez „Widzi stawki i kwoty”: null) */
  preview: string | null;
}

export interface OfferRequestSendResult {
  approvalId: string;
  offer: OfferDetail;
  /** właściciel z prawem do wysyłki: zgoda przy prośbie, oferta wychodzi od razu (DECISIONS 2026-10-09) */
  approvedNow: boolean;
}

export interface OfferFieldDiff {
  field: OfferContentField;
  changed: boolean;
  before: string | null;
  after: string | null;
}

export interface OfferVersionsDiff {
  from: number;
  to: number;
  fields: OfferFieldDiff[];
}

/**
 * „Główna oferta” klienta (kafelek w profilu): ostatnio przyjęta oferta. `hoursPlanned` = godziny (liczba,
 * np. 40 albo 12.5), `plannedSeconds` = to samo w sekundach. `projectId` = projekt klienta o tej samej nazwie co
 * oferta (bez powiązania w bazie; null, gdy brak), `usedSeconds` = czas tego projektu (null bez projektu).
 */
export interface MainOffer {
  offerId: string;
  number: number;
  title: string;
  acceptedAt: Date;
  plannedSeconds: number | null;
  hoursPlanned: number | null;
  package: string | null;
  projectId: string | null;
  projectName: string | null;
  usedSeconds: number | null;
}

// ── Prezentacja i porównanie wersji ─────────────────────────────────────────

/** Wartość pola treści do podglądu i porównania (PL). `rate` wymaga „Widzi stawki i kwoty” (wołający decyduje). */
export function offerFieldText(field: OfferContentField, c: NormalizedOfferContent | OfferContentDto): string | null {
  switch (field) {
    case 'estimatedSeconds':
      return c.estimatedSeconds ? formatDuration(c.estimatedSeconds) : null;
    case 'rate':
      // „150 zł/h”, z groszami tylko gdy są („150,50 zł/h”) — jak w podsumowaniu oferty
      return c.rate ? `${formatPLN(c.rate, { currency: c.currency, decimals: !/^\d+(\.0+)?$/.test(c.rate) })}/h` : null;
    default: {
      const v = c[field];
      return typeof v === 'string' && v.trim() !== '' ? v : null;
    }
  }
}

/** Porównanie dwóch wersji pole po polu. Bez `seesRates` pole stawki jest pominięte. */
export function diffOfferContent(
  before: NormalizedOfferContent | OfferContentDto,
  after: NormalizedOfferContent | OfferContentDto,
  opts: { seesRates: boolean },
): OfferFieldDiff[] {
  return OFFER_CONTENT_FIELDS.filter((f) => opts.seesRates || f !== 'rate').map((field) => {
    const b = offerFieldText(field, before);
    const a = offerFieldText(field, after);
    const changed = field === 'rate' ? b !== a || before.currency !== after.currency : b !== a;
    return { field, changed, before: b, after: a };
  });
}

export interface TextDiffLine {
  kind: 'same' | 'added' | 'removed';
  text: string;
}

/** Porównanie tekstu linia po linii (LCS). Bardzo długie teksty: całość usunięta + dodana. */
export function diffTextLines(before: string | null, after: string | null): TextDiffLine[] {
  const a = before ? before.split('\n') : [];
  const b = after ? after.split('\n') : [];
  if (a.length * b.length > 1_000_000) {
    return [
      ...a.map((text) => ({ kind: 'removed' as const, text })),
      ...b.map((text) => ({ kind: 'added' as const, text })),
    ];
  }
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    const row = lcs[i] as number[];
    const next = lcs[i + 1] as number[];
    for (let j = m - 1; j >= 0; j--) {
      row[j] = a[i] === b[j] ? (next[j + 1] as number) + 1 : Math.max(next[j] as number, row[j + 1] as number);
    }
  }
  const out: TextDiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: 'same', text: a[i] as string });
      i++;
      j++;
    } else if ((lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0)) {
      out.push({ kind: 'removed', text: a[i] as string });
      i++;
    } else {
      out.push({ kind: 'added', text: b[j] as string });
      j++;
    }
  }
  while (i < n) out.push({ kind: 'removed', text: a[i++] as string });
  while (j < m) out.push({ kind: 'added', text: b[j++] as string });
  return out;
}

/** Pierwsza niepusta linia tekstu bez znaczników listy (np. podsumowanie zakresu), skrócona. */
export function firstLine(text: string | null | undefined, max = 120): string {
  const line =
    (text ?? '')
      .split('\n')
      .map((l) =>
        l
          .replace(/^\s*(?:[-*+]|\d+[.)])\s+/, '')
          .replace(/^#+\s*/, '')
          .trim(),
      )
      .find((l) => l !== '') ?? '';
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
}
